import React, { useMemo } from 'react'
import {
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    useReactFlow,
} from '@xyflow/react'
import './branch-edge.scss'
import { useJourneyContext } from '../../context'
import { getJourneyDisplayItemType } from '../../../../_utils/journeys'
import { SPLIT_LAYOUT_CONSTANTS } from '../../layout/elk-config'
import { JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import { useEdgeActions } from '../shared/use-edge-actions'
import { EdgeActionPopover } from '../shared/edge-action-popover'
import type { ElkEdgeSection } from 'elkjs/lib/elk-api'
import { getBranchingNodeData } from '../../utils/graph/traversal'

/**
 * Build an SVG path string from ELK edge sections with rounded corners.
 *
 * At each bend point the path shortens the incoming and outgoing straight
 * segments by CORNER_RADIUS pixels and inserts a quadratic Bézier curve
 * (Q command) through the original bend point, producing a smooth arc.
 * When a segment is too short for the full radius the radius is clamped
 * so the path never overshoots.
 *
 * Exported so custom-edge can reuse it for convergence edges.
 */
export function buildPathFromSections(sections: ElkEdgeSection[]): string {
    const r = SPLIT_LAYOUT_CONSTANTS.CORNER_RADIUS
    const parts: string[] = []

    for (const section of sections) {
        const points = [
            section.startPoint,
            ...(section.bendPoints ?? []),
            section.endPoint,
        ]

        if (points.length < 2) continue

        parts.push(`M ${points[0].x} ${points[0].y}`)

        if (points.length === 2) {
            parts.push(`L ${points[1].x} ${points[1].y}`)
            continue
        }

        for (let i = 1; i < points.length - 1; i++) {
            const prev = points[i - 1]
            const curr = points[i]
            const next = points[i + 1]

            // Distances to prev and next points
            const dPrev = Math.hypot(curr.x - prev.x, curr.y - prev.y)
            const dNext = Math.hypot(next.x - curr.x, next.y - curr.y)

            // Clamp radius so we don't overshoot either segment
            const cr = Math.min(r, dPrev / 2, dNext / 2)

            if (cr < 1) {
                // Segment too short for a curve — just draw straight
                parts.push(`L ${curr.x} ${curr.y}`)
                continue
            }

            // Point where the curve starts (on the incoming segment)
            const startX = curr.x + ((prev.x - curr.x) / dPrev) * cr
            const startY = curr.y + ((prev.y - curr.y) / dPrev) * cr

            // Point where the curve ends (on the outgoing segment)
            const endX = curr.x + ((next.x - curr.x) / dNext) * cr
            const endY = curr.y + ((next.y - curr.y) / dNext) * cr

            parts.push(`L ${startX} ${startY}`)
            parts.push(`Q ${curr.x} ${curr.y} ${endX} ${endY}`)
        }

        // Final straight segment to the end point
        const last = points[points.length - 1]
        parts.push(`L ${last.x} ${last.y}`)
    }

    return parts.join(' ')
}

export const BranchEdge = ({
    id,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    source,
    target,
    data,
    ...props
}: EdgeProps<JourneyEdge>) => {
    const [state] = useJourneyContext()
    const { getNode } = useReactFlow<JourneyNode>()

    const branchData = data

    const { edgeActionOptions, showEdgeActions, setShowEdgeActions, handleAddNode } = useEdgeActions({
        source,
        target,
        variantId: branchData?.branchKey,
    })

    // Extract ELK-computed bend points if available
    const elkSections = branchData?.elkSections
    const hasELKRouting = elkSections && elkSections.length > 0

    // Determine branch index and count from the source branching step node
    // so we can calculate horizontal offset for each branch edge (fallback only)
    const sourceNode = getNode(source)
    const branchInfo = useMemo(() => {
        if (!sourceNode) return { index: 0, count: 1 }
        const branchingData = getBranchingNodeData(sourceNode)
        if (!branchingData) return { index: 0, count: 1 }
        const branches = branchingData.branches
        const idx = branches.findIndex(b => branchData && b.key === branchData.branchKey)
        return { index: idx >= 0 ? idx : 0, count: branches.length || 1 }
    }, [sourceNode, branchData])

    // Build edge path: prefer ELK bend points, fall back to manual construction
    const edgePath = useMemo(() => {
        if (hasELKRouting) {
            return buildPathFromSections(elkSections!)
        }

        // Fallback: manual path construction (pre-ELK behavior)
        if (process.env.NODE_ENV === 'development') {
            console.warn('BranchEdge: falling back to manual path construction for edge', id)
        }

        const LANE_SPREAD = 80
        const centerOffset = (branchInfo.count - 1) / 2
        const laneOffset = (branchInfo.index - centerOffset) * LANE_SPREAD
        const laneX = sourceX + laneOffset
        const midY = sourceY + (targetY - sourceY) * 0.35
        const convergeY = targetY - 25

        if (Math.abs(laneOffset) < 2) {
            return `M ${sourceX} ${sourceY} L ${sourceX} ${targetY}`
        }

        return [
            `M ${sourceX} ${sourceY}`,
            `L ${sourceX} ${midY}`,
            `L ${laneX} ${midY}`,
            `L ${laneX} ${convergeY}`,
            `L ${targetX} ${convergeY}`,
            `L ${targetX} ${targetY}`,
        ].join(' ')
    }, [sourceX, sourceY, targetX, targetY, hasELKRouting, elkSections, branchInfo, id])

    // --- Button and label positioning ---
    //
    // Label: positioned just below the split node port, at the start of the
    // edge's first vertical drop. This keeps labels predictable and close to
    // the split node, never competing with buttons.
    //
    // Button: midpoint of the main vertical segment leading to the target.
    // No clamping — the symmetric layout ensures adequate spacing.

    const { labelX, labelY, addButtonX, addButtonY } = useMemo(() => {
        if (hasELKRouting) {
            const section = elkSections![0]
            const bends = section.bendPoints ?? []

            // Label Y: just below the first horizontal bend (or port if straight)
            const edgeLabelY = section.startPoint.y + SPLIT_LAYOUT_CONSTANTS.LABEL_VERTICAL_OFFSET

            if (bends.length >= 4) {
                // Full lane routing (empty variant → join target):
                //   port → bend[0] → bend[1] (lane center) → bend[2] → bend[3] → endPoint
                // Label at lane center X so labels spread out even when ports are clamped
                return {
                    labelX: bends[1].x,
                    labelY: edgeLabelY,
                    addButtonX: bends[1].x,
                    addButtonY: (bends[1].y + bends[2].y) / 2,
                }
            }

            if (bends.length >= 2) {
                // L-shape (variant has intermediate nodes):
                //   port → bend[0] → bend[1] (target/lane center) → endPoint
                // Label at target center X (which is the lane center)
                return {
                    labelX: bends[1].x,
                    labelY: edgeLabelY,
                    addButtonX: section.endPoint.x,
                    addButtonY: (bends[1].y + section.endPoint.y) / 2,
                }
            }

            // Straight vertical
            return {
                labelX: section.startPoint.x,
                labelY: edgeLabelY,
                addButtonX: section.endPoint.x,
                addButtonY: (section.startPoint.y + section.endPoint.y) / 2,
            }
        }

        // Fallback
        return {
            labelX: sourceX,
            labelY: sourceY + SPLIT_LAYOUT_CONSTANTS.LABEL_VERTICAL_OFFSET,
            addButtonX: sourceX,
            addButtonY: (sourceY + targetY) / 2,
        }
    }, [hasELKRouting, elkSections, sourceX, sourceY, targetY])

    // Hide arrow markers on edges targeting convergence dots for a cleaner look.
    const targetNode = getNode(target)
    const targetIsConvergence = targetNode?.data?.step?.type === 'placeholder'
        && targetNode?.data?.uiMeta?.placeholderRole === 'convergence'

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={targetIsConvergence ? undefined : props.markerEnd} markerStart={props.markerStart} />
            <EdgeLabelRenderer>
                {/* Branch label near the source end */}
                {branchData && (
                    <div
                        className="branch-edge-label"
                        style={{
                            transform: `translate(-50%, 0%) translate(${labelX}px, ${labelY}px)`,
                        }}
                    >
                        <span className="branch-edge-label-text">
                            {branchData.branchLabel}{branchData.percentage != null && branchData.percentage > 0 ? ` (${branchData.percentage}%)` : ''}
                        </span>
                    </div>
                )}

                {/* Add step button */}
                <EdgeActionPopover
                    addButtonX={addButtonX}
                    addButtonY={addButtonY}
                    edgeActionOptions={edgeActionOptions}
                    showEdgeActions={showEdgeActions}
                    setShowEdgeActions={setShowEdgeActions}
                    handleAddNode={handleAddNode}
                    mode={state.mode}
                    itemType={getJourneyDisplayItemType(state.journey, state.domain.ecommConfig?.itemType)}
                />
            </EdgeLabelRenderer>
        </>
    )
}
